/**
 * دعوتِ دوست به اتاق (docs/friend-invite.md) از نگاهِ وب.
 *
 * کسی که لینکِ اتاق (‎/play?room=615265) را باز می‌کند معمولاً هنوز حساب ندارد، و بی حساب هم
 * می‌نشیند — جز سرِ میزِ ورودی‌دار. دو کار این‌جاست:
 *
 *  - **پیش‌نمایش** (GET /v1/rooms/{code}): لابی می‌گوید چه کسی او را به کدام میز دعوت کرده و
 *    ثبت‌نام چه هدیه‌ای دارد — [[InviteGate]].
 *  - **بلیط**: همان پاسخ بلیطِ امضاشده‌ی میزبان را دارد. هفت روز در حافظه‌ی مرورگر می‌مانَد و
 *    سرِ تأییدِ ایمیل با /v1/account/verify به سرور می‌رسد؛ سرور آن‌جا هدیه‌ی هر دو را می‌دهد.
 *    اتاق تا آن وقت شاید شروع شده باشد، ولی بلیط می‌مانَد.
 *
 * همین کلید و قالب را site/auth.js می‌خوانَد: ثبت‌نام و تأیید در /account.html است.
 */
import { httpUrl } from "./config";
import { signal } from "./signal";

export const INVITE_KEY = "bazivan.invite";
export const INVITE_TTL_MS = 7 * 86_400_000;

/** همان قالبِ بلیطِ سرور (friend_invite.go): «میزبان.اتاق.زمان.امضا». */
const TICKET = /^[A-Za-z0-9_-]+\.\d{6}\.\d+\.[A-Za-z0-9_-]+$/;

export type RoomInvite = {
  code: string;
  game: string;
  game_fa: string;
  host: string;
  seats: number;
  taken: number;
  open: boolean;
  locked: boolean;
  /** هدیه‌ی ثبت‌نام برای هر کدام؛ ۰ = بی هدیه. */
  coins: number;
  /** ورودیِ سکه‌ایِ میز؛ نبودن = رایگان. میزِ ورودی‌دار حسابِ فعال می‌خواهد — مهمان نمی‌نشیند. */
  stake?: number;
  invite?: string;
};

/** پیش‌نمایشِ اتاقِ همین نشانی: اتاق، «gone» (کد درست بود ولی اتاق دیگر نیست)، یا null (هنوز نه / شبکه). */
export const roomInvite = signal<RoomInvite | "gone" | null>(null);

/**
 * «بی حساب بنشین» (کاربر، ۲۵ اکتبر ۲۰۲۶: «دریافت‌کننده بدونِ حتی حساب هم بتواند بازی کند»): کسی که با لینکِ اتاق
 * آمده دکمه را زد. مهمان همان دم ساخته می‌شود — نه با بازکردنِ صفحه ([[lazy-guest-web]]، client.seatAsGuest) — و
 * App اتاقِ نشانی را بی ثبت‌نام می‌پیوندد. ثبت‌نام همچنان راهِ نگه‌داشتنِ سکه‌ها و هدیه‌ی دعوت است.
 */
export const guestSeat = signal(false);

type KV = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function storage(): KV | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/** بلیط را تا تأییدِ حساب نگه می‌دارد. بلیطِ تازه‌تر جای قبلی را می‌گیرد — آخرین دعوت. */
export function rememberInvite(ticket: string, kv: KV | null = storage(), now = Date.now()): void {
  if (!kv || !TICKET.test(ticket) || ticket.length > 160) return;
  try {
    kv.setItem(INVITE_KEY, JSON.stringify({ t: ticket, at: now }));
  } catch { /* حافظه پر یا بسته */ }
}

/** بلیطِ زنده، یا "" اگر نیست یا هفت روزش گذشته. */
export function pendingInvite(kv: KV | null = storage(), now = Date.now()): string {
  if (!kv) return "";
  try {
    const raw = kv.getItem(INVITE_KEY);
    if (!raw) return "";
    const v = JSON.parse(raw) as { t?: unknown; at?: unknown };
    if (typeof v.t === "string" && TICKET.test(v.t) && typeof v.at === "number" && now - v.at <= INVITE_TTL_MS) return v.t;
    kv.removeItem(INVITE_KEY);
  } catch { /* قالبِ خراب: انگار نیست */ }
  return "";
}

export function forgetInvite(kv: KV | null = storage()): void {
  try {
    kv?.removeItem(INVITE_KEY);
  } catch { /* بسته */ }
}

/** پیش‌نمایش را می‌گیرد، بلیطش را نگه می‌دارد و در [[roomInvite]] می‌گذارد. */
export async function loadRoomInvite(code: string, f: typeof fetch = fetch, kv: KV | null = storage()): Promise<RoomInvite | "gone" | null> {
  if (!/^\d{6}$/.test(code)) return null;
  let out: RoomInvite | "gone" | null = null;
  try {
    const res = await f(httpUrl("/v1/rooms/" + code), { headers: { Accept: "application/json" } });
    if (res.status === 404) out = "gone";
    else if (res.ok) {
      const r = (await res.json()) as RoomInvite;
      if (r && typeof r.code === "string") {
        out = r;
        if (r.invite) rememberInvite(r.invite, kv);
      }
    }
  } catch { /* شبکه: دروازه همان متنِ همیشگی را نشان می‌دهد */ }
  roomInvite.value = out;
  return out;
}
